import {useState} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, SyncButton, SyncPhase, TextField} from "../../ui/components";
import {CioLinkInfoTooltip} from "../../components/tooltips";
import {CioSource, useFetchCioTemplate} from "../../apis/requests/templates/fetchCioTemplate";
import {CioTemplatePreview, ReachTemplateContentChannel} from "../../apis/requests/templates/types";
import {FormValues, MESSAGING_CHANNELS, SOURCE_LABELS, SOURCES} from "./templateFormSchema";

type SyncVariant = "media" | "landing_page";

type TemplateSourceSyncRowProps = {
  onSyncResult?: (result: CioTemplatePreview) => void;
  variant?: SyncVariant;
  label?: string;
};

const TemplateSourceSyncRow = ({onSyncResult, variant = "media", label}: TemplateSourceSyncRowProps) => {
  const {control, getValues, setValue} = useFormContext<FormValues>();
  const isLandingPageVariant = variant === "landing_page";
  const sourceField = isLandingPageVariant ? "landing_page_source" : "source";
  const sourceLinkField = isLandingPageVariant ? "landing_page_source_link" : "source_link";
  const source = useWatch({control, name: sourceField});
  const sourceLink = useWatch({control, name: sourceLinkField});
  const contentChannel = useWatch({control, name: "content_channel"});
  const mediaHasIndependentLandingPage = !isLandingPageVariant && contentChannel === "mms_with_landing_page";
  const showsCioSyncRow = source === "cio_studio" || source === "cio_regular";
  const cioMutation = useFetchCioTemplate();
  const [missingSmsTemplateId, setMissingSmsTemplateId] = useState<string | null>(null);
  const [syncPhase, setSyncPhase] = useState<SyncPhase>("idle");

  const trimmedLink = (sourceLink ?? "").trim();
  const linkError =
    showsCioSyncRow && trimmedLink && !trimmedLink.startsWith("https://fly.customer.io/")
      ? "Expected a Customer.io link"
      : null;

  const handleSync = async () => {
    if (!showsCioSyncRow) return;
    setSyncPhase("syncing");
    setMissingSmsTemplateId(null);
    const sourceLinkValue = getValues(sourceLinkField);
    const contentChannel = getValues("content_channel");
    try {
      const data = await cioMutation.mutateAsync({
        source: source as CioSource,
        source_link: sourceLinkValue,
        content_channel: contentChannel as ReachTemplateContentChannel,
        sync_target: isLandingPageVariant ? "landing_page" : undefined,
      });
      if (isLandingPageVariant) {
        setValue("landing_page_body", data.landing_page_body ?? "", {shouldDirty: true});
        onSyncResult?.(data);
        setSyncPhase("synced");
        return;
      }
      setValue("template_key", data.template_key, {shouldDirty: true});
      setValue("channel_message_code", data.channel_message_code ?? null, {shouldDirty: true});
      if (contentChannel === "email") {
        setValue("body", data.body, {shouldDirty: true});
        setValue("pre_header", data.pre_header ?? "", {shouldDirty: true});
        setValue("email_domain_type", data.email_domain_type ?? null, {shouldDirty: true});
        setValue("subject", data.subject ?? "", {shouldDirty: true});
      } else if (contentChannel === "mms" || contentChannel === "mms_with_landing_page") {
        setValue("body", data.body, {shouldDirty: true});
      } else if (contentChannel === "sms_with_landing_page") {
        setValue("landing_page_body", data.landing_page_body ?? "", {shouldDirty: true});
      }
      if (MESSAGING_CHANNELS.has(contentChannel)) {
        if (data.subject !== null) {
          setValue("subject", data.subject, {shouldDirty: true});
        } else if (data.channel_message_code) {
          setValue("subject", data.message ?? "", {shouldDirty: true});
          setMissingSmsTemplateId(data.channel_message_code);
        }
      }
      onSyncResult?.(data);
      setSyncPhase("synced");
    } catch {
      setSyncPhase("idle");
    }
  };

  const syncDisabled = !!linkError || !trimmedLink;

  return (
    <>
      <Box sx={{width: "100%"}}>
        <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
          {label ?? (isLandingPageVariant ? "Landing page source *" : "Source *")}
        </Text>
        <Controller
          name={sourceField}
          control={control}
          render={({field}) => (
            <Select
              {...field}
              value={field.value ?? "manual"}
              fullWidth
              size="small"
              displayEmpty
              onChange={(e) => {
                field.onChange(e.target.value as FormValues["source"]);
                if (isLandingPageVariant) {
                  setValue("landing_page_body", "", {shouldDirty: true});
                  setValue("landing_page_source_link", "", {shouldDirty: true});
                } else {
                  setValue("template_key", "", {shouldDirty: true});
                  setValue("body", "", {shouldDirty: true});
                  if (!mediaHasIndependentLandingPage) {
                    setValue("landing_page_body", "", {shouldDirty: true});
                  }
                  setValue("subject", "", {shouldDirty: true});
                  setValue("pre_header", "", {shouldDirty: true});
                  setValue("source_link", "", {shouldDirty: true});
                  setValue("channel_message_code", null, {shouldDirty: true});
                  setValue("email_domain_type", null, {shouldDirty: true});
                }
                setSyncPhase("idle");
              }}
            >
              {SOURCES.map((s) => (
                <MenuItem key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      {showsCioSyncRow && (
        <Box sx={{width: "100%", mt: 2}}>
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5, mb: 0.5}}>
            <Text size={13} sx={{color: Colors.grey._700}}>
              Customer.io link *
            </Text>
            <CioLinkInfoTooltip source={source} />
            {linkError && (
              <Text size={12} sx={{color: Colors.red._900, ml: 0.5}}>
                {linkError}
              </Text>
            )}
          </Box>
          <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
            <Controller
              name={sourceLinkField}
              control={control}
              render={({field: {ref, ...field}, fieldState}) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  size="small"
                  sx={{flex: 1, minWidth: 0}}
                  error={!!linkError || !!fieldState.error}
                  onChange={(e) => {
                    field.onChange(e);
                    if (syncPhase === "synced") setSyncPhase("idle");
                  }}
                />
              )}
            />
            <SyncButton phase={syncPhase} onClick={handleSync} disabled={syncDisabled} />
          </Container>
          {missingSmsTemplateId && (
            <Text size={12} sx={{color: Colors.red._800, mt: 0.5, display: "block"}}>
              Backend is missing an SMS template message
            </Text>
          )}
        </Box>
      )}
    </>
  );
};

export default TemplateSourceSyncRow;
